import React, { ReactElement, useCallback, useMemo, useState } from "react";
import { useRouter } from "next/router";
import styles from "./styles.module.scss";
import { TPostsGrid } from "@page-builder/ts/generated";
import { getDomainPosts } from "src/api/fetchDomainPosts";
import { useDomainContext } from "@hooks/useDomainContext";
import FilterSidebar, { FilterOption } from "./filterSidebar";
import PostCard from "./postCard";
import Pagination from "./pagination";
import { DomainPostItemType } from "pages/api/fetchDomainPosts";
import SkeletonGrid from "./skeletonGrid";
import NoPostsCard from "./noPostsCard";

export const DEFAULT_PER_PAGE = 9;

export const getFeaturedPosts = (
    featuredPosts: { id?: number; title?: string }[] | undefined,
): string => {
    if (
        featuredPosts &&
        Array.isArray(featuredPosts) &&
        featuredPosts.length > 0
    ) {
        return featuredPosts.map((post) => post.id).join(",");
    }
    return "";
};

export default function PostsGrid({
    props = {},
}: {
    props?: TPostsGrid["props"];
}): ReactElement {
    const router = useRouter();
    const { domainPosts, pageBuilderBlogPost } = useDomainContext();

    const {
        postType: postTypeAsProp,
        title,
        description,
        hideFilters = false,
        hidePagination = false,
        postsPerPage: postsPerPageProp,
        readMoreText = "Read More",
        resetFilterText = "Reset Filters",
        noPostsText = "No posts found.",
        noPostsImage,
        postFilters: filterConfig,
        filterByText,
        hideCardTag = false,
        hideReadMoreButton = false,
        hideFactorsSection = false,
        showReadMorePosts = false,
        urlSyncEnabled = false,
        featuredPosts,
        applyFeaturedFilter = false,
    } = props;
    const postType = postTypeAsProp ? postTypeAsProp : "blog-type_post";
    const postsPerPage = postsPerPageProp ? postsPerPageProp : DEFAULT_PER_PAGE;
    const featuredPostsIds = getFeaturedPosts(featuredPosts);

    // When showReadMorePosts is enabled on a blog post page, we fetch postsPerPage + 1
    // and filter out the current post from the list
    const isOnPostPage = showReadMorePosts && pageBuilderBlogPost !== null;

    const filterCurrentPost = (postsList: DomainPostItemType[]) => {
        if (!isOnPostPage || !pageBuilderBlogPost?.id) return postsList;
        return postsList
            .filter((post) => {
                return post.id !== pageBuilderBlogPost.id;
            })
            .slice(0, postsPerPage);
    };

    // Get posts data for this specific postType from the dictionary
    const postsData = domainPosts?.[postType] ?? null;
    const initialDomainPosts = postsData?.data
        ? filterCurrentPost(postsData?.data)
        : null;
    const initialTotalCount = postsData?.totalCount ?? 0;

    const perPage = postsPerPage;

    const initialPage = (() => {
        if (!urlSyncEnabled) return 1;
        const parsed = parseInt(router.query.page as string, 10);
        return Number.isFinite(parsed) && parsed > 1 ? parsed : 1;
    })();

    const initialSlugs = (() => {
        if (!urlSyncEnabled) return [];
        const raw = router.query.filters as string | undefined;
        return raw ? raw.split(",").map((slug) => slug.trim()) : [];
    })();

    const [posts, setPosts] = useState<DomainPostItemType[]>(
        initialDomainPosts ?? [],
    );
    const [totalCount, setTotalCount] = useState(initialTotalCount);
    const [loading, setLoading] = useState(!initialDomainPosts);
    const [currentPage, setCurrentPage] = useState<number>(initialPage);
    const [activeSlugs, setActiveSlugs] = useState<string[]>(initialSlugs);

    const syncUrl = useCallback(
        (page: number, slugs: string[]) => {
            if (!urlSyncEnabled || typeof window === "undefined") return;

            const params = new URLSearchParams(window.location.search);

            if (page > 1) {
                params.set("page", String(page));
            } else {
                params.delete("page");
            }

            if (slugs.length > 0) {
                params.set("filters", slugs.join(","));
            } else {
                params.delete("filters");
            }

            const queryString = params.toString();

            const newUrl = `${window.location.pathname}${
                queryString ? `?${queryString}` : ""
            }${window.location.hash}`;

            window.history.pushState(window.history.state, "", newUrl);
        },
        [urlSyncEnabled],
    );

    // Build a real, crawlable URL for a given page so pagination renders as
    // <a href> links that search engines can follow (matches syncUrl params).
    const buildHref = useCallback(
        (page: number) => {
            const path = router.asPath.split("?")[0].split("#")[0];

            const params = new URLSearchParams();

            if (page > 1) {
                params.set("page", String(page));
            }

            if (activeSlugs.length > 0) {
                params.set("filters", activeSlugs.join(","));
            }

            const queryString = params.toString();

            const newUrl = `${path}${queryString ? `?${queryString}` : ""}`;

            return newUrl;
        },
        [router.asPath, activeSlugs],
    );

    const fetchPosts = useCallback(
        async (page: number, slugs: string[]) => {
            setLoading(true);
            try {
                const { data, totalCount: count } = await getDomainPosts(
                    {
                        type: postType || "blog-type_post", // default to "blog-type_post" if postType is undefined
                        taxonomySlug: slugs,
                        limit: String(perPage),
                        page: page - 1, // UI is 1-indexed; API is 0-indexed
                        ...(featuredPostsIds
                            ? { "filter[id]": featuredPostsIds }
                            : {}),
                        ...(applyFeaturedFilter
                            ? { "filter[featured]": true }
                            : {}),
                    },
                    undefined, // client-side call
                );
                setPosts(data ?? []);
                setTotalCount(count);
            } finally {
                setLoading(false);
            }
        },
        [postType, perPage, featuredPostsIds, applyFeaturedFilter],
    );

    // ── Filter toggle ──
    const handleFilterToggle = useCallback(
        (slug: string) => {
            const next = activeSlugs.includes(slug)
                ? activeSlugs.filter((s) => s !== slug)
                : [...activeSlugs, slug];
            setActiveSlugs(next);
            setCurrentPage(1);
            syncUrl(1, next);
            void fetchPosts(1, next);
        },
        [activeSlugs, fetchPosts, syncUrl],
    );

    // ── Reset filters ──
    const handleReset = useCallback(() => {
        setActiveSlugs([]);
        setCurrentPage(1);
        syncUrl(1, []);
        void fetchPosts(1, []);
    }, [fetchPosts, syncUrl]);

    // ── Page change ──
    const handlePageChange = useCallback(
        (page: number) => {
            setCurrentPage(page);
            syncUrl(page, activeSlugs);
            void fetchPosts(page, activeSlugs);
        },
        [activeSlugs, fetchPosts, syncUrl],
    );

    const showFilters = !hideFilters && filterConfig && filterConfig.length > 0;

    const showPagination = !hidePagination;

    const filters: FilterOption[] = useMemo(
        () =>
            (filterConfig ?? []).map((f) => ({
                title: f.title ?? "",
                slug: f.slug ?? "",
                icon: f.icon,
            })),
        [filterConfig],
    );

    return (
        <div className={styles.postsGridContainer}>
            {title && (
                <div
                    className={styles.title}
                    dangerouslySetInnerHTML={{ __html: title }}
                />
            )}
            {description && (
                <div
                    className={styles.description}
                    dangerouslySetInnerHTML={{ __html: description }}
                />
            )}

            <div className={styles.contentArea}>
                {showFilters && (
                    <FilterSidebar
                        filters={filters}
                        activeSlugs={activeSlugs}
                        onToggle={handleFilterToggle}
                        onReset={handleReset}
                        resetText={resetFilterText}
                        title={filterByText}
                    />
                )}

                <div className={styles.gridArea}>
                    {loading ? (
                        <SkeletonGrid
                            count={perPage}
                            className={`${styles.grid} ${
                                showFilters ? styles.gridWithFilters : ""
                            }`}
                        />
                    ) : posts.length === 0 ? (
                        <NoPostsCard text={noPostsText} image={noPostsImage} />
                    ) : (
                        <div
                            className={`${styles.grid} ${
                                showFilters ? styles.gridWithFilters : ""
                            }`}
                        >
                            {posts.map((post) => (
                                <PostCard
                                    key={post.id}
                                    post={post}
                                    type={postType}
                                    readMoreText={readMoreText}
                                    hideCardTag={hideCardTag}
                                    hideReadMoreButton={hideReadMoreButton}
                                    hideFactorsSection={hideFactorsSection}
                                />
                            ))}
                        </div>
                    )}

                    {showPagination && totalCount > 0 && (
                        <Pagination
                            currentPage={currentPage}
                            totalCount={totalCount}
                            perPage={perPage}
                            onPageChange={handlePageChange}
                            buildHref={urlSyncEnabled ? buildHref : undefined}
                        />
                    )}
                </div>
            </div>
        </div>
    );
}
